import React, { useEffect } from "react";

import { Alert } from "~/components/alert";
import { Button } from "~/components/button";
import { Divider } from "~/components/divider";
import { Input } from "~/components/input";
import { SkeletonBox } from "~/components/skeleton/box";
import { copyToClipboard } from "~/tools/copy-to-clipboard";

import { useErrorMessage } from "../../../components/_notification-error";
import { DocLink } from "../../../components/react/docLink/DocLink";
import { DrawerTitle, ExplorationDrawer } from "../../../components/react/drawer/ExplorationDrawer";
import { GenericErrorMessage } from "../../../components/react/error/GenericErrorMessage";
import { DONT_AUTO_DISMISS, notification } from "../../../components/react/notification/Notification";
import { useMutation } from "../../../queries/react-query";
import { createServiceToken } from "../../../queries/servicetoken";

export interface ServiceTokenDrawerState {
	cluster?: string;
	name: string;
}

export interface ServiceTokenDrawerProps {
	when: undefined | ServiceTokenDrawerState;
}

export const ServiceTokenDrawer: React.FC<ServiceTokenDrawerProps> = ({ when }) => (
	<ExplorationDrawer headerTitle={<DrawerTitle title="New service token" />} layout="compact" when={when}>
		{(state) => <CreateServiceToken {...state} />}
	</ExplorationDrawer>
);

const CreateServiceToken = (state: ServiceTokenDrawerState) => {
	const { displayErrorMessage } = useErrorMessage();

	const {
		mutate: createToken,
		data: token,
		error,
	} = useMutation({
		...createServiceToken,
		onError: (err) => {
			displayErrorMessage({
				message: `Failed to create subject or service token ${state.name}`,
				error: err,
			});
		},
	});

	useEffect(() => {
		createToken({ cluster: state.cluster, name: state.name });
	}, [createToken, state.cluster, state.name]);

	if (error != null) {
		return <GenericErrorMessage error={error} />;
	}

	return (
		<div className="space-y-6">
			<Alert>
				Use the service token to ingest telemetry and authorization data for this cluster - the token can be used to
				authorize a SUSE Observability agent installation. Or use the shell export alternative below for a CLI
				installation via helm.
			</Alert>

			<CopyToClipboardInput label="Your new service token" name="service-token" token={token} variant="primary" />

			<Divider>OR</Divider>

			<CopyToClipboardInput
				label="Shell export alternative"
				name="service-token-export"
				token={token == null ? undefined : `export SERVICE_TOKEN=${token}`}
				variant="secondary"
			/>

			<div>
				<Header>Need more help?</Header>

				<p className="m-0 mt-2">
					If you need further help understanding how service tokens work or listing them using the CLI, please read{" "}
					<DocLink capture="noop" slug="ui-service-tokens">
						our documentation
					</DocLink>
					.
				</p>
			</div>
		</div>
	);
};

const Header: React.FC<React.PropsWithChildren> = (props) => (
	<h3 {...props} className="m-0 font-medium text-[15px]/[22px] text-gray-900" />
);

const CopyToClipboardInput: React.FC<{
	label: string;
	name: string;
	variant: "primary" | "secondary";
	token: undefined | string;
}> = ({ label, name, token, variant }) => (
	<div>
		<Header>{label}</Header>

		<SkeletonBox className="mt-3" when={token}>
			<Input disabled={token == null} name={name} readOnly type="password" value={token} />
		</SkeletonBox>

		<SkeletonBox className="mt-4" when={token}>
			<Button
				className="w-full uppercase"
				disabled={token == null}
				onClick={() => {
					const [failure, success] = copyToClipboard(token ?? "");
					if (failure == null) {
						notification.success({ message: success });
					} else {
						notification.error({
							message: failure,
							duration: DONT_AUTO_DISMISS,
						});
					}
				}}
				variant={variant}
			>
				Copy to clipboard
			</Button>
		</SkeletonBox>
	</div>
);
